import React from "react";

import type { CustomerCard } from "../hooks/useCustomerCards";

type Props = {
  active: boolean;
  cards: CustomerCard[];
  loading: boolean;
  error: string | null;
  selectedCustomerId: string | null;
  onSelectCustomer: (id: string) => void;
  mode: "index" | "detail";
  onBack?: () => void;
  orderNumbers?: string[];
  selectedOrderNumber?: string | null;
  onSelectOrderNumber?: (val: string | null) => void;
  showList?: boolean;
  inventoryList?: React.ReactNode;
};

export function CustomersPage({
  active,
  cards,
  loading,
  error,
  selectedCustomerId,
  onSelectCustomer,
  mode,
  onBack,
  orderNumbers,
  selectedOrderNumber,
  onSelectOrderNumber,
  showList,
  inventoryList,
}: Props): JSX.Element | null {
  if (!active) return null;

  const selected = cards.find((c) => c.id === selectedCustomerId) || null;

  return (
    <div className="w-full max-w-[1600px] mx-auto px-4 md:px-6 pb-6">
      <div className="flex items-center justify-between mt-4 mb-3">
        <div className="flex items-center gap-3">
          {mode === "detail" && onBack && (
            <button className="gw-btn gw-btn-secondary" onClick={onBack}>← Till kundkort</button>
          )}
          <h1 className="gw-h2 m-0">{mode === "detail" && selected ? selected.name : "Kunder"}</h1>
        </div>
        <div className="text-sm text-muted">
          {loading ? "Laddar kunder…" : `${cards.length} kunder`}
        </div>
      </div>

      {error && (
        <div className="gw-banner gw-banner--warn mb-3">
          Kunde inte ladda kunder: {error}
        </div>
      )}

      <div className="grid gap-3 grid-cols-[repeat(auto-fill,minmax(240px,1fr))] mb-4">
        {cards.map((c) => {
          const isActive = c.id === selectedCustomerId;
          return (
            <button
              key={c.id}
              type="button"
              className={`gw-card text-left transition border ${isActive ? "border-primary shadow-card" : "border-border"}`}
              onClick={() => onSelectCustomer(c.id)}
            >
              <div className="flex items-start gap-2">
                <div className="font-semibold text-lg text-text truncate">{c.name}</div>
                {isActive && <span className="gw-badge ml-auto">Vald</span>}
              </div>
              <div className="mt-2 flex items-center gap-4 text-sm">
                <div>
                  <div className="text-muted text-xs">Enheter</div>
                  <div className="text-text text-base font-semibold">{c.total ?? "–"}</div>
                </div>
                <div>
                  <div className="text-muted text-xs">Klarmarkerade</div>
                  <div className="text-text text-base font-semibold">{c.completed ?? "–"}</div>
                </div>
                <div>
                  <div className="text-muted text-xs">Order</div>
                  <div className="text-text text-base font-semibold">{c.orderCount ?? "–"}</div>
                </div>
              </div>
              {c.statsStatus && c.statsStatus !== "ready" && (
                <div className="mt-2 text-xs text-muted" role="status">
                  {c.statsStatus === "loading" ? "Laddar statistik…" : c.statsStatus === "stale"
                    ? "Visar senast hämtad statistik" : "Statistik saknas"}
                </div>
              )}
            </button>
          );
        })}

        {!loading && cards.length === 0 && (
          <div className="gw-card text-muted">Inga kunder hittades.</div>
        )}
      </div>

      {mode === "detail" && selected && (
        <div className="mt-4">
          <div className="flex items-center gap-3 mb-3">
            <label className="text-sm text-muted">Ordernummer</label>
            <select
              className="gw-select w-[220px]"
              value={selectedOrderNumber || ""}
              onChange={(e) => onSelectOrderNumber?.(e.target.value || null)}
            >
              <option value="">Visa alla</option>
              {(orderNumbers || []).map((ord) => (
                <option key={ord} value={ord}>{ord}</option>
              ))}
            </select>
          </div>
          {showList && inventoryList}
        </div>
      )}
    </div>
  );
}
